// Bits Reward Handler - processes cheer events and triggers rewards
import useBitsStore, { EFFECT_PRODUCTS, COSMETIC_PRODUCTS, PERK_THRESHOLDS } from '../store/bitsStore'
import { useTerrariumStore } from '../store/terrariumStore'
import useSettingsStore from '../store/settingsStore'
import { twitchApi } from './twitchApi'

const sendChat = async (message) => {
  console.log(`[Bits] ${message}`)
  await twitchApi.sendMessage(message)
}

class BitsRewardHandler {
  constructor() {
    this.initialized = false
  }

  // Initialize - hook into cheer events AND extension PubSub
  init() {
    if (this.initialized) return
    this.initialized = true

    // Listen for channel cheers (fallback if extension not installed)
    twitchApi.onCheer((cheerEvent) => {
      this.handleCheer(cheerEvent)
    })

    // Listen for extension PubSub messages (from extension panel redemptions)
    twitchApi.onExtensionMessage((message) => {
      this.handleExtensionRedemption(message)
    })

    // Connect to SSE stream for real-time bits events from extension panel
    this.connectSSE()

    console.log('BitsRewardHandler initialized')
  }

  // Connect to SSE for real-time bits events from the server
  connectSSE() {
    const broadcasterLogin = twitchApi.broadcasterLogin
    if (!broadcasterLogin) {
      // Retry once broadcaster is known
      setTimeout(() => this.connectSSE(), 2000)
      return
    }

    const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:3001'
    const url = `${API_BASE}/twitch/terrarium/api/broadcaster/${broadcasterLogin}/bits/events`

    const eventSource = new EventSource(url)
    eventSource.onmessage = (event) => {
      try {
        const message = JSON.parse(event.data)
        if (message.type === 'connected') {
          console.log('SSE connected for bits events')
          return
        }
        this.handleExtensionRedemption(message)
      } catch (e) {
        console.warn('Failed to parse SSE message:', e)
      }
    }
    eventSource.onerror = () => {
      console.warn('SSE connection lost, reconnecting in 5s...')
      eventSource.close()
      setTimeout(() => this.connectSSE(), 5000)
    }
  }

  // Handle redemptions from extension panel (via SSE or PubSub broadcast)
  handleExtensionRedemption(message) {
    console.log('[Bits] SSE message received:', message)

    const settings = useSettingsStore.getState().settings
    if (!settings.enableBitsFeatures) {
      console.log('[Bits] Bits features disabled, ignoring')
      return
    }

    const { type, sku, bits, userId, username, totalBits } = message
    const bitsStore = useBitsStore.getState()

    // Update local bits cache
    if (totalBits) {
      bitsStore.recordCheer(userId, username, bits)
    }

    if (type === 'effect') {
      // Trigger the visual effect
      const store = useTerrariumStore.getState()
      const effect = EFFECT_PRODUCTS.find(e => e.id === sku)
      console.log('[Bits] Effect lookup for SKU:', sku, '→', effect ? effect.name : 'NOT FOUND')
      if (effect) {
        bitsStore.triggerEffect(sku, userId, username)
        bitsStore.addBitsNotification(effect.icon || '💎', `${username} used ${effect.name}!`, bits)

        // Handle server-side effects
        if (sku === 'rain') store.waterAllPlants(userId, username)
        if (sku === 'mega_bloom') {
          const entity = store.getEntity(userId)
          if (entity && entity.type === 'plant') store.bloomPlant(userId, true)
        }
      }
    } else if (type === 'cosmetic') {
      // Add to local cosmetics cache so !equip works immediately
      bitsStore.addUserCosmetic(userId, sku)
      bitsStore.addBitsNotification('✨', `${username} unlocked ${sku}!`, bits)
    } else {
      bitsStore.addBitsNotification('💎', `${username} used ${bits} bits!`, bits)
    }
  }

  async handleCheer(cheerEvent) {
    const settings = useSettingsStore.getState().settings
    if (!settings.enableBitsFeatures) return

    const { userId, username, bits, isAnonymous } = cheerEvent
    const bitsStore = useBitsStore.getState()
    const store = useTerrariumStore.getState()

    // Record the cheer in the database
    const totalBits = await bitsStore.recordCheer(userId, username, bits)

    const displayName = isAnonymous ? 'Anonymous' : username

    // Add notification
    bitsStore.addBitsNotification('💎', `${displayName} used ${bits} bits!`, bits)

    // ============================================
    // Tier 2: Instant Effects (auto-trigger by amount)
    // ============================================
    const effect = this.matchEffect(bits)
    if (effect) {
      await this.triggerEffect(effect, userId, username, store, bitsStore)
    }

    // ============================================
    // Tier 3: Check perk thresholds
    // ============================================
    if (totalBits) {
      await this.checkPerkUnlocks(userId, username, totalBits, bitsStore)
    }

    // Send thank you message
    if (!isAnonymous) {
      if (effect) {
        await sendChat(`💎 @${username} used ${bits} bits and triggered ${effect.name}! ${effect.icon}`)
      } else {
        await sendChat(`💎 @${username} used ${bits} bits! Total: ${totalBits} bits`)
      }
    }
  }

  // Match a cheer amount to the highest-matching instant effect
  matchEffect(bits) {
    // Find the best matching effect (highest bits that doesn't exceed cheered amount)
    const sorted = [...EFFECT_PRODUCTS].sort((a, b) => b.bits - a.bits)
    return sorted.find(e => bits >= e.bits) || null
  }

  // Trigger an instant effect
  async triggerEffect(effect, userId, username, store, bitsStore) {
    bitsStore.triggerEffect(effect.id, userId, username)

    switch (effect.id) {
      case 'rain':
        // Water all plants
        await store.waterAllPlants(userId, username)
        break

      case 'mega_bloom':
        // Force bloom (boosts to 100% growth) the cheerer's plant if they have one
        const entity = store.getEntity(userId)
        if (entity && entity.type === 'plant') {
          store.bloomPlant(userId, true)
        }
        break

      case 'fireworks':
      case 'swarm':
      case 'earthquake':
        // Visual-only effects handled by the activeEffects state in bitsStore
        break
    }
  }

  // Check if user crossed any perk thresholds
  async checkPerkUnlocks(userId, username, totalBits, bitsStore) {
    for (const perk of PERK_THRESHOLDS) {
      if (totalBits >= perk.threshold) {
        // Check if we already notified (simple check via cosmetics)
        const hasIt = bitsStore.hasCosmetic(userId, `perk_${perk.id}`)
        if (!hasIt) {
          bitsStore.addBitsNotification(perk.icon, `${username} unlocked ${perk.name}!`, 0)
          await sendChat(`⭐ @${username} reached ${perk.threshold} total bits and unlocked ${perk.name}! ${perk.icon}`)
        }
      }
    }
  }
}

export const bitsRewardHandler = new BitsRewardHandler()
